import React from "react";

import { cn } from "../../../common/u1/cn";
import { FileIcon } from "../../../../assets/ui/icons";
import { CSV_ACCEPT, CSV_FORMAT_LINE, FileDropzone } from "./FileDropzone";
import { REQUIRED_CSV_HEADERS } from "./financialSetupFields";
import csvTemplate from "../../../../assets/files/financial_document_sample.csv";

/* Method 3 — the CSV template, filled in and uploaded. */
export const FinancialCsvPanel = ({ csv, className = "" }) => {
  const { file, setFile, clear, missingHeaders, parsing, error, rows } = csv;

  return (
    <div className={className}>
      <div className="flex justify-end">
        <a
          href={csvTemplate}
          download="sampleFinancialDocument.csv"
          className={cn(
            "inline-flex h-9 items-center gap-1.5 rounded-lg px-3",
            "bg-[var(--orange)] text-[13px] font-semibold text-white font-sans",
            "transition-colors hover:bg-[var(--orange-hover)]",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-[var(--orange)] focus-visible:ring-offset-2"
          )}
        >
          <FileIcon size={13} />
          Download CSV Template
        </a>
      </div>

      <div
        className={cn(
          "mt-3.5 rounded-[14px] border border-[var(--line)] bg-white",
          "p-4 sm:p-6"
        )}
      >
        <p className="m-0 mb-2 text-[13px] font-bold text-[var(--ink)] font-sans">
          Upload Income Statement CSV File
        </p>

        {parsing ? (
          <p
            role="status"
            aria-live="polite"
            className="m-0 text-[13px] text-[var(--muted)] font-sans"
          >
            Reading the file…
          </p>
        ) : file ? (
          <div
            className={cn(
              "flex flex-wrap items-center justify-between gap-3 rounded-[12px]",
              "border border-[var(--line)] bg-[var(--field-inset)] px-4 py-3"
            )}
          >
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[var(--ink)] font-sans">
              {file.name}
              {rows.length > 0 && (
                <span className="ml-2 font-normal text-[var(--muted)]">
                  {rows.length} {rows.length === 1 ? "year" : "years"}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={clear}
              className={cn(
                "rounded px-1 text-[12px] font-bold text-[var(--muted)] font-sans",
                "transition-colors hover:text-[var(--coral-deep)]",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-[var(--coral)]"
              )}
            >
              Remove
            </button>
          </div>
        ) : (
          <FileDropzone
            label="Income statement CSV"
            accept={CSV_ACCEPT}
            formatHint={CSV_FORMAT_LINE}
            files={[]}
            onChange={(next) => setFile(next?.[0] || null)}
          />
        )}

        {error && (
          <p
            role="alert"
            className="m-0 mt-3 text-[12.5px] leading-[1.55] font-semibold text-[var(--coral-deep)] font-sans"
          >
            {error}
          </p>
        )}

        {/* Naming them is the difference between a fixable file and a dead
            end — the live screen only disabled its button. */}
        {missingHeaders.length > 0 && (
          <div
            className={cn(
              "mt-3 rounded-[12px] border border-[var(--line)]",
              "bg-[var(--field-inset)] px-4 py-3"
            )}
          >
            <p className="m-0 text-[12px] font-bold text-[var(--ink)] font-sans">
              Missing columns
            </p>
            <p className="m-0 mt-1 break-words text-[12px] leading-[1.6] text-[var(--muted)] font-sans">
              {missingHeaders.join(", ")}
            </p>
          </div>
        )}

        <p className="m-0 mt-3 text-[11.5px] text-[var(--muted)] font-sans">
          The template carries all {REQUIRED_CSV_HEADERS.length} columns, named
          exactly as we read them.
        </p>
      </div>
    </div>
  );
};

export default FinancialCsvPanel;
